<template>
  <TTabsToggle
    :header="$t('forms.rating_captions')"
    toggle-show
    :default-displayed="showCaptions"
    no-move
    data-testid="rating-captions-tab"
    @showBlock="showCaptions = true"
    @hideBlock="showCaptions = false"
  >
    <menu-content-container border-full :padding-left-right="8">
      <content-row>
        <TInput
          v-model="captionTextLeft"
          :label="$t('forms.rating_left_caption')"
          data-testid="rating-caption-text-left"
        />
      </content-row>
      <content-row>
        <TInput
          v-model="captionTextRight"
          :label="$t('forms.rating_right_caption')"
          data-testid="rating-caption-text-right"
        />
      </content-row>
      <content-row>
        <TButtonGroup
          data-testid="caption-layout-group"
          :label="$t('forms.rating_caption_layout')"
          :changed="layout.changed"
          :reset-tooltip="$t('forms.reset_value')"
          @reset="layout.reset"
        >
          <TGroupButton
            v-tooltip="$t('forms.rating_caption_layout_below')"
            data-testid="caption-layout-below"
            data="below"
            small
            :active="layout.model === 'below'"
            @button-clicked="setLayout('below')"
          >
            <CaptionBelowIcon />
          </TGroupButton>
          <TGroupButton
            v-tooltip="$t('forms.rating_caption_layout_inline')"
            data-testid="caption-layout-inline"
            data="inline"
            small
            :active="layout.model === 'inline'"
            @button-clicked="setLayout('inline')"
          >
            <CaptionInlineIcon />
          </TGroupButton>
        </TButtonGroup>
        <TButtonGroup
          v-if="customMobile"
          data-testid="caption-layout-mobile-group"
          :label="$t('forms.rating_caption_layout_mobile')"
          :changed="layoutMobile.changed"
          :reset-tooltip="$t('forms.reset_value')"
          @reset="layoutMobile.reset"
        >
          <TGroupButton
            v-tooltip="$t('forms.rating_caption_layout_below')"
            data-testid="caption-layout-mobile-below"
            data="below"
            small
            :active="layoutMobile.model === 'below'"
            @button-clicked="layoutMobile.model = 'below'"
          >
            <CaptionBelowIcon />
          </TGroupButton>
          <TGroupButton
            v-tooltip="$t('forms.rating_caption_layout_inline')"
            data-testid="caption-layout-mobile-inline"
            data="inline"
            small
            :active="layoutMobile.model === 'inline'"
            @button-clicked="layoutMobile.model = 'inline'"
          >
            <CaptionInlineIcon />
          </TGroupButton>
        </TButtonGroup>
      </content-row>
      <content-row>
        <t-switch
          v-model="customMobile"
          :label="$t('forms.rating_caption_layout_customize_mobile')"
          data-testid="caption-layout-customize-mobile"
        />
      </content-row>
    </menu-content-container>
    <menu-content-container :padding-left-right="8">
      <FontSizeColor
        :data-testid="`captions-text-settings`"
        block-name="captionsAttributes"
        no-border
      />
    </menu-content-container>
    <PaddingForm
      :external-padding="padding.model"
      external-usage
      :label="$t('forms.padding_label')"
      :changed="padding.changed"
      :default-value="padding.defaultValue"
      data-testid="captions-padding-form"
      @paddingChanged="padding.model = $event"
      @reset="padding.reset"
    />
  </TTabsToggle>
</template>

<script setup lang="ts">
  import FontSizeColor from "./fontSizeColorRating.vue";
  import PaddingForm from "../paddingForm.vue";

  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useBlockAttr } from "@/composables/useBlockAttr";
  import { TopolRatingBlock } from "@/schemas/template/blocks/ratingBlockSchema";

  import {
    TTabsToggle,
    TInput,
    TButtonGroup,
    TGroupButton,
    TSwitch,
  } from "@topol/twig-ui";
  import CaptionBelowIcon from "./CaptionBelowIcon";
  import CaptionInlineIcon from "./CaptionInlineIcon";
  import { computed, ref, watch } from "vue";

  const mjmlStore = useMjmlStore();
  const { str, nullable } = useBlockAttr({
    mode: "active-group",
    group: "captionsAttributes",
  });

  const customMobile = ref(false);

  const padding = nullable("padding");
  const layout = str("layout", "below");
  const layoutMobile = str("layoutMobile", "below");

  const captions = computed(() => {
    const block = mjmlStore.getActiveBlock as TopolRatingBlock;
    if (!block || !block.captionsAttributes) return {} as any;
    return block.captionsAttributes as any;
  });

  const captionTextLeft = computed({
    get() {
      return mjmlStore.getActiveBlockPropString("captionTextLeft") || "";
    },
    set(val) {
      mjmlStore.updateBlockProp({
        prop: "captionTextLeft",
        value: val,
      });
    },
  });

  const captionTextRight = computed({
    get() {
      return mjmlStore.getActiveBlockPropString("captionTextRight") || "";
    },
    set(val) {
      mjmlStore.updateBlockProp({
        prop: "captionTextRight",
        value: val,
      });
    },
  });

  const showCaptions = computed({
    get() {
      return captions.value.show;
    },
    set(val) {
      mjmlStore.updateBlockProp({
        prop: "captionsAttributes",
        value: {
          ...captions.value,
          show: val,
        },
      });
    },
  });

  function setLayout(val: "below" | "inline") {
    const synced = !customMobile.value;
    mjmlStore.updateBlockProp({
      prop: "captionsAttributes",
      value: {
        ...captions.value,
        layout: val,
        ...(synced ? { layoutMobile: val } : {}),
      },
    });
  }

  watch(customMobile, (val) => {
    if (!val) {
      mjmlStore.updateBlockProp({
        prop: "captionsAttributes",
        value: { ...captions.value, layoutMobile: layout.model },
      });
    }
  });
</script>
